// Pricing-document level configuration: what happens to a package (non-linear) price when a
// booking does not fit inside the band that carries it.
//
// Pure by design: no Firebase, no React. The pricing engine lives in the backend - this app
// only authors `resourcepricings/{id}` documents, so everything here is naming and labelling.
//
// THE CASE THIS COVERS. A pricing slot with `nonlinearpricing: true` carries a `prices[]`
// package table (30 min, 1 h, 1,5 h ...) instead of a plain hourly price. When a booking runs
// from one slot into the next, the package band OVERLAPS the booking without CONTAINING it,
// and there is no single package that fits. `packagebandmode` picks what to do:
//
//   absent     Today's behaviour at every venue. Each band charges its own package for its
//              own overlap, so a crossing booking pays the sum of two shorter packages -
//              which is typically more than the one long package it should have got.
//   'linear'   Those minutes bill at the band's price €/h through the ordinary charge path.
//   'ignore'   The pricing yields nothing at all and the next pricing by priority is tried.
//
// Note the scope of 'ignore': it drops the WHOLE pricing document, not just the one band.
// That is why the field lives on the document and not on the slot.
//
// THE DEFAULT IS THE ABSENCE OF THE KEY, not a value meaning "default". Firestore's
// set({merge: true}) never removes a key, so getting back to it has to write a delete
// sentinel - see the `packagebandmode` handling in firebase.js. An empty string, or the
// PACKAGE_BAND_DEFAULT sentinel below, left behind in the document would be a value where
// the backend expects nothing.
//
// WHY THERE IS A SENTINEL AT ALL. rjsf v6 treats a `oneOf` branch's `const` as a default
// (experimental_defaultFormStateBehavior.constAsDefaults, which defaults to on), so a schema
// offering only ['linear', 'ignore'] silently pre-selects 'linear' the moment the dialog
// opens - and an admin who opened a pricing to change its priority would save a band mode
// they never chose. Every `oneOf: [{const, title}]` select in this app behaves that way; here
// it would quietly change what venues charge, so the dropdown carries an explicit first
// option instead, and the write path turns it back into an absent field.
export const PACKAGE_BAND_DEFAULT = 'default'

// The dropdown says "Oletus:" because the option has to announce itself as the default. The
// summary card does not - it already has a label of its own, and two colons in one line read
// as a mistake.
export const PACKAGE_BAND_DEFAULT_LABEL = 'Oletus: kumpikin osio laskuttaa oman pakettihintansa'

export const PACKAGE_BAND_DEFAULT_SHORT = 'Kumpikin osio laskuttaa oman pakettihintansa'

// The hinnasto summary card's label for this line. Shorter than the dialog's field title,
// which spells out the whole rule - the card renders '<title>: <label>' and the dialog's own
// title already carries a colon of its own. Exported rather than repeated because four pricing
// containers render the same line.
export const PACKAGE_BAND_CARD_TITLE = 'Epälineaarinen hinnoittelu'

export const PACKAGE_BAND_MODES = [
  {
    value: 'linear',
    // Shown in the dropdown.
    label: 'Tuntihinta: pakettiosion minuutit laskutetaan sen tuntihinnalla',
    // Shown in the hinnasto summary card, where there is one line to spare.
    short: 'Tuntihinta ylimenevältä osalta',
  },
  {
    value: 'ignore',
    label: 'Ohita: hinnoittelu ei tuota hintaa, seuraava prioriteetti yritetään',
    short: 'Ohita hinnoittelu',
  },
]

// Reads the stored field tolerantly: anything that is not a real mode - the sentinel, an empty
// string, a value from some future build - reads as the default. The two real modes are the
// only things worth acting on, and a mode nobody recognises must not read as one of them.
export const packageBandMode = (pricing) => {
  const stored = pricing && pricing.packagebandmode
  const found = PACKAGE_BAND_MODES.find((m) => m.value === stored)
  return found ? found.value : null
}

export const packageBandModeLabel = (pricing) => {
  const mode = packageBandMode(pricing)
  if (!mode) return PACKAGE_BAND_DEFAULT_SHORT
  return PACKAGE_BAND_MODES.find((m) => m.value === mode).short
}

// The oneOf for the rjsf schema. Built here so the dropdown and the summary card cannot drift
// apart. The sentinel goes FIRST because rjsf pre-selects the first branch's const (see above)
// and the first option is the only safe thing to be pre-selected.
//
// Pair it with `default: PACKAGE_BAND_DEFAULT` in the schema: rjsf's mui SelectWidget renders
// an extra blank placeholder row whenever `schema.default === undefined`, which would offer two
// visually different ways to say the same thing.
export const packageBandModeOneOf = () => [
  { const: PACKAGE_BAND_DEFAULT, title: PACKAGE_BAND_DEFAULT_LABEL },
  ...PACKAGE_BAND_MODES.map((m) => ({ const: m.value, title: m.label })),
]
